import React, { useLayoutEffect, useRef, useState } from "react"
import Note, { Title } from "@kit/design/note"
import { useMounted } from "hooks/use-mounted"
import { log } from "utils/log"
import ReactDOM from "react-dom"
import PropTypes from "prop-types"
import stylis from "stylis"
import "./index.css"

function useDOMElement(type) {
  const [element] = useState(() => document.createElement(type))
  return element
}

export const HTMLTemplate = ({
  html,
  css,
  selector,
  editorId,
  // A function that will be called whenever new content is mounted into the
  // HTML Template.
  onMount = () => {},
  children
}) => {
  const [error, setError] = useState(false)
  const container = useRef()
  const content = useDOMElement("div")
  const mounted = useMounted(onMount)

  const cb = useRef()

  useLayoutEffect(() => {
    cb.current = onMount
  }, [onMount])

  useLayoutEffect(() => {
    setError(false)
    if (!container.current) return
    const node = container.current.querySelector(selector)
    if (!node) {
      setError(true)
      log("template-error")
      return
    }
    node.innerHTML = ""
    node.appendChild(content)
    if (mounted.current) {
      cb.current()
    }
  }, [html, css, content, selector, mounted])

  let scope = ".html-template"

  if (editorId) {
    scope = scope + `.${editorId}`
  }

  return (
    <React.Fragment>
      {css && (
        <style
          dangerouslySetInnerHTML={{
            __html: stylis(scope, css)
          }}
        />
      )}
      {error && (
        <Note type="warning">
          <Title>Template Error</Title>
          There is an error in the code for your custom email template.
        </Note>
      )}
      <div
        ref={container}
        className={`html-template ${editorId || ""} w-full`}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {/* Do not use portal when testing to be able to check editor content in tests */}
      {process.env.NODE_ENV === "test"
        ? children
        : ReactDOM.createPortal(children, content)}
    </React.Fragment>
  )
}

HTMLTemplate.propTypes = {
  html: PropTypes.string.isRequired,
  css: PropTypes.string,
  selector: PropTypes.string.isRequired,
  editorId: PropTypes.string,
  onMount: PropTypes.func,
  children: PropTypes.any
}
